"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { sum } from "@/lib/stats/descriptive";
import { downloadText } from "@/lib/tool-kit/download";
import {
  DASH,
  decimals,
  type NumberFormatter,
  percentText,
} from "@/lib/tool-kit/research-design/format";
import { labelsFor, toCsv } from "@/lib/tool-kit/research-design/matrix";
import {
  type AllocatedStratum,
  type AllocationMethod,
  type AllocationOutcome,
  allocate,
  parseStrata,
  type Stratum,
} from "@/lib/tool-kit/research-design/stratified";

/** English strings. A translated content file overrides these through `ui`. */
export const STRATIFIED_SAMPLE_CALCULATOR_STRINGS = {
  nLabel: "Total sample size (n)",
  nPlaceholder: "400",
  nHintLead: "Need to choose n first?",
  nHintLink: "Work it out with the Sample Size Calculator.",
  strataLabel: "Strata — one per line: name, population size, SD (optional)",
  strataHint:
    "Separate the columns with tabs or commas; a spreadsheet paste works as it is, and a header row is skipped. The SD is the spread of your main variable within each stratum and is only needed for Neyman allocation.",
  strataPlaceholder: "Arts\t2350\t6\nBusiness\t3880\t8\nEngineering\t1640\t4",
  loadExample: "Load example",
  clear: "Clear",
  exampleStrata:
    "Faculty\tStudents\tSD of weekly paid-work hours\nArts\t2350\t6\nBusiness\t3880\t8\nEngineering\t1640\t4\nScience\t4130\t5",
  exampleN: "400",
  readAs: "{strata} with a total population of {population}.",
  strataCount: "one: {count} stratum|other: {count} strata",
  skippedLines:
    "one: Line {lines} could not be read — it needs a name, a whole-number population size and, optionally, an SD.|other: Lines {lines} could not be read — each needs a name, a whole-number population size and, optionally, an SD.",
  showParsed: "Show the strata as read",
  strataFallback: "Stratum {n}",
  methodLegend: "Allocation",
  methodProportional: "Proportional",
  methodEqual: "Equal",
  methodNeyman: "Neyman (optimal)",
  formulaProportional:
    "nₕ = n × Nₕ ÷ N — every stratum is sampled at the same rate, so the sample mirrors the population.",
  formulaEqual:
    "nₕ = n ÷ H — the same number from each of the H strata, for comparing strata with one another.",
  formulaNeyman:
    "nₕ = n × NₕSₕ ÷ Σ NₖSₖ — more units where strata are larger or more variable, which gives the smallest standard error for a fixed n.",
  results: "Results",
  enterPrompt:
    "Enter a total sample size and at least two strata, or load the example.",
  problemTooFew: "Enter at least two strata.",
  problemN: "Enter a whole-number sample size of at least 1.",
  problemTooLarge:
    "A sample of {n} is larger than the whole population ({population}).",
  problemSd:
    "Neyman allocation needs an SD for every stratum, and at least one above zero.",
  colStratum: "Stratum",
  colPopulation: "Population (Nₕ)",
  colSd: "SD (Sₕ)",
  colShare: "Share of population",
  colQuota: "Exact quota",
  colSample: "Sample (nₕ)",
  colFraction: "Sampling fraction",
  colWeight: "Weight (Nₕ ÷ nₕ)",
  total: "Total",
  censusMark: "{sample} (whole stratum)",
  censusNote:
    "one: {count} stratum is smaller than its quota, so all of it is taken and the remaining units are shared among the others.|other: {count} strata are smaller than their quotas, so each is taken whole and the remaining units are shared among the others.",
  smallNote:
    "one: {count} stratum gets fewer than two units — too few to estimate its variance.|other: {count} strata get fewer than two units — too few to estimate their variances.",
  roundingNote:
    "Whole units come from the largest-remainder method: each stratum gets the whole part of its quota, then the units left over go to the largest fractional parts (ties to the larger stratum), so the sizes add up to exactly n.",
  compareHeading: "The three allocations side by side",
  seLabel: "Standard error of the mean",
  seNote:
    "From your SDs, with the finite population correction: SE² = Σ Wₕ² × Sₕ² ÷ nₕ × (1 − nₕ ÷ Nₕ), where Wₕ = Nₕ ÷ N.",
  downloadCsv: "Download allocation (CSV)",
  csvFile: "stratified-allocation.csv",
} as const;

type Strings = typeof STRATIFIED_SAMPLE_CALCULATOR_STRINGS;

const METHODS: readonly AllocationMethod[] = [
  "proportional",
  "equal",
  "neyman",
];
const METHOD_KEYS = {
  proportional: { name: "methodProportional", formula: "formulaProportional" },
  equal: { name: "methodEqual", formula: "formulaEqual" },
  neyman: { name: "methodNeyman", formula: "formulaNeyman" },
} as const;

function problemText(
  outcome: Extract<AllocationOutcome, { ok: false }>,
  n: number,
  population: number,
  strings: Strings,
  fmt: NumberFormatter
): string {
  if (outcome.problem === "too-few-strata") {
    return strings.problemTooFew;
  }
  if (outcome.problem === "sample-too-small") {
    return strings.problemN;
  }
  if (outcome.problem === "sample-exceeds-population") {
    return t(strings.problemTooLarge, {
      n: fmt(n),
      population: fmt(population),
    });
  }
  return strings.problemSd;
}

function allocationRow(
  name: string,
  stratum: Stratum,
  row: AllocatedStratum,
  population: number,
  strings: Strings,
  fmt: NumberFormatter
): string[] {
  return [
    name,
    fmt(stratum.size),
    percentText(fmt, stratum.size / population),
    fmt(row.quota, { maximumFractionDigits: 2 }),
    row.census
      ? t(strings.censusMark, { sample: fmt(row.sample) })
      : fmt(row.sample),
    percentText(fmt, row.fraction, 2),
    row.weight === null ? DASH : fmt(row.weight, { maximumFractionDigits: 2 }),
  ];
}

function Comparison({
  strata,
  names,
  n,
  strings,
  fmt,
}: {
  strata: readonly Stratum[];
  names: readonly string[];
  n: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const outcomes = METHODS.map((method) => allocate(strata, n, method));
  const cell = (outcome: AllocationOutcome, h: number) =>
    outcome.ok ? fmt(outcome.strata[h].sample) : DASH;
  const se = (outcome: AllocationOutcome) =>
    outcome.ok && outcome.standardError !== null
      ? decimals(fmt, outcome.standardError, 3)
      : DASH;
  const hasSe = outcomes.some(
    (outcome) => outcome.ok && outcome.standardError !== null
  );
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.compareHeading}</h3>
      <DataTable
        head={[
          strings.colStratum,
          ...METHODS.map((method) => strings[METHOD_KEYS[method].name]),
        ]}
        rows={[
          ...names.map((name, h) => [
            name,
            ...outcomes.map((outcome) => cell(outcome, h)),
          ]),
          ...(hasSe ? [[strings.seLabel, ...outcomes.map(se)]] : []),
        ]}
      />
      {hasSe ? <p className={HINT}>{strings.seNote}</p> : null}
    </div>
  );
}

export default function StratifiedSampleCalculatorWidget() {
  const strings = useToolUi(STRATIFIED_SAMPLE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [nText, setNText] = useState("");
  const [raw, setRaw] = useState("");
  const [method, setMethod] = useState<AllocationMethod>("proportional");

  const parsed = parseStrata(raw);
  const { strata } = parsed;
  const names = labelsFor(
    strata.map((stratum) => stratum.name),
    strata.length,
    (n) => t(strings.strataFallback, { n })
  );
  const population = sum(strata.map((stratum) => stratum.size));
  const n = parseIntegerInput(nText);
  const outcome = n === null ? null : allocate(strata, n, method);
  const ready = n !== null && strata.length > 0;

  function download() {
    if (!outcome?.ok) {
      return;
    }
    const head = [
      strings.colStratum,
      strings.colPopulation,
      strings.colSd,
      strings.colQuota,
      strings.colSample,
      strings.colFraction,
      strings.colWeight,
    ];
    const rows = outcome.strata.map((row, h) => [
      names[h],
      strata[h].size,
      strata[h].sd,
      row.quota,
      row.sample,
      row.fraction,
      row.weight,
    ]);
    downloadText(
      strings.csvFile,
      toCsv([head, ...rows]),
      "text/csv;charset=utf-8"
    );
  }

  return (
    <>
      <section className="space-y-4">
        <div className="max-w-sm">
          <TextField
            hint={
              <>
                {strings.nHintLead}{" "}
                <Link
                  className="text-primary underline underline-offset-2"
                  href="/tools/sample-size-calculator"
                >
                  {strings.nHintLink}
                </Link>
              </>
            }
            id="ss-n"
            label={strings.nLabel}
            numeric
            onChange={setNText}
            placeholder={strings.nPlaceholder}
            value={nText}
          />
        </div>
        <TextAreaField
          hint={strings.strataHint}
          id="ss-strata"
          label={strings.strataLabel}
          onChange={setRaw}
          placeholder={strings.strataPlaceholder}
          rows={6}
          value={raw}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setRaw(strings.exampleStrata);
              setNText(strings.exampleN);
            }}
            type="button"
          >
            {strings.loadExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            disabled={raw === "" && nText === ""}
            onClick={() => {
              setRaw("");
              setNText("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        {strata.length > 0 ? (
          <details className="space-y-2">
            <summary className={`${LABEL} cursor-pointer`}>
              {strings.showParsed}
            </summary>
            <p className={HINT}>
              {t(strings.readAs, {
                strata: tPlural(strings.strataCount, locale, strata.length, {
                  count: fmt(strata.length),
                }),
                population: fmt(population),
              })}
            </p>
            <DataTable
              head={[strings.colStratum, strings.colPopulation, strings.colSd]}
              rows={strata.map((stratum, h) => [
                names[h],
                fmt(stratum.size),
                stratum.sd === null ? DASH : fmt(stratum.sd),
              ])}
            />
          </details>
        ) : null}
        {parsed.skipped.length > 0 ? (
          <Notice>
            {tPlural(strings.skippedLines, locale, parsed.skipped.length, {
              lines: parsed.skipped.map((line) => fmt(line)).join(", "),
            })}
          </Notice>
        ) : null}
        <PillGroup
          label={strings.methodLegend}
          name="ss-method"
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: strings[METHOD_KEYS[value].name],
          }))}
          value={method}
        />
        <p className="font-mono text-muted-foreground text-sm">
          {strings[METHOD_KEYS[method].formula]}
        </p>
      </section>

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {ready ? null : <Notice>{strings.enterPrompt}</Notice>}
        {ready && outcome && !outcome.ok ? (
          <ErrorNotice>
            {problemText(outcome, n, population, strings, fmt)}
          </ErrorNotice>
        ) : null}
        {ready && outcome?.ok ? (
          <AllocationTable
            download={download}
            names={names}
            outcome={outcome.strata}
            population={population}
            strata={strata}
            strings={strings}
          />
        ) : null}
        {ready && strata.length >= 2 ? (
          <Comparison
            fmt={fmt}
            n={n}
            names={names}
            strata={strata}
            strings={strings}
          />
        ) : null}
      </section>
    </>
  );
}

function AllocationTable({
  strata,
  names,
  outcome,
  population,
  strings,
  download,
}: {
  strata: readonly Stratum[];
  names: readonly string[];
  outcome: readonly AllocatedStratum[];
  population: number;
  strings: Strings;
  download: () => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const census = outcome.filter((row) => row.capped).length;
  const small = outcome.filter((row) => row.sample < 2).length;
  const totalSample = sum(outcome.map((row) => row.sample));
  return (
    <div className="space-y-3">
      <DataTable
        head={[
          strings.colStratum,
          strings.colPopulation,
          strings.colShare,
          strings.colQuota,
          strings.colSample,
          strings.colFraction,
          strings.colWeight,
        ]}
        rows={[
          ...outcome.map((row, h) =>
            allocationRow(names[h], strata[h], row, population, strings, fmt)
          ),
          [
            strings.total,
            fmt(population),
            percentText(fmt, 1),
            fmt(totalSample),
            fmt(totalSample),
            percentText(fmt, totalSample / population, 2),
            DASH,
          ],
        ]}
      />
      {census > 0 ? (
        <Notice>
          {tPlural(strings.censusNote, locale, census, { count: fmt(census) })}
        </Notice>
      ) : null}
      {small > 0 ? (
        <Notice>
          {tPlural(strings.smallNote, locale, small, { count: fmt(small) })}
        </Notice>
      ) : null}
      <p className={HINT}>{strings.roundingNote}</p>
      <button className={BUTTON_SECONDARY} onClick={download} type="button">
        {strings.downloadCsv}
      </button>
    </div>
  );
}
